import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * HeritageHero
 * - Stacked-layered crossfade (NOT AnimatePresence) → zero blank gap on transition
 *   because every image stays mounted; we only animate the active layer's opacity.
 * - Soft Ken Burns slow zoom on the active image.
 * - Manual slide controls (dots) with auto-advance every 5.5s.
 * - Respects prefers-reduced-motion.
 * - All images eagerly preloaded so the first cycle is silky.
 */

const DEFAULT_IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1681545303529-b6beb2e19f02?auto=format&fit=crop&q=80&w=1400",
    alt: "African headwraps and cultural fashion",
    label: "Heritage",
  },
  {
    src: "https://images.unsplash.com/photo-1524414621493-7dec026782c3?auto=format&fit=crop&q=80&w=1400",
    alt: "African textiles and artisan craft",
    label: "Craft",
  },
  {
    src: "https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&q=80&w=1400",
    alt: "Ankara print pattern detail",
    label: "Ankara",
  },
  {
    src: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?auto=format&fit=crop&q=80&w=1400",
    alt: "Braiding service",
    label: "Service",
  },
  {
    src: "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&q=80&w=1400",
    alt: "Jollof rice with grilled chicken",
    label: "Cuisine",
  },
  {
    src: "https://images.unsplash.com/photo-1602471017055-fd2cc9bbf3a3?auto=format&fit=crop&q=80&w=1400",
    alt: "African beaded jewellery",
    label: "Beads",
  },
  {
    src: "https://images.unsplash.com/photo-1604066867775-43f48e3957d8?auto=format&fit=crop&q=80&w=1400",
    alt: "Handcrafted African pottery",
    label: "Pottery",
  },
  {
    src: "https://images.unsplash.com/photo-1503714327954-5e2bef3148f3?auto=format&fit=crop&q=80&w=1400",
    alt: "African market spices and vibrant produce",
    label: "Market",
  },
];

export const HeritageHero = ({ images = DEFAULT_IMAGES }) => {
  const [active, setActive] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) setReducedMotion(true);
  }, []);

  // Preload all images so the rotation is silky-smooth from the first cycle.
  useEffect(() => {
    images.forEach((it) => {
      const img = new Image();
      img.src = it.src;
    });
  }, [images]);

  useEffect(() => {
    if (reducedMotion) return;
    const t = setInterval(() => setActive((i) => (i + 1) % images.length), 5500);
    return () => clearInterval(t);
  }, [images.length, reducedMotion]);

  return (
    <div className="relative w-full h-full overflow-hidden rounded-md bg-[#F3EFE9]" data-testid="heritage-hero-section">
      {/* STACK every slide. Each is positioned absolute, only the active one is fully opaque.
          This eliminates blank flicker during transitions because images never unmount. */}
      {images.map((it, idx) => (
        <motion.div
          key={idx}
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: idx === active ? 1 : 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          style={{ zIndex: idx === active ? 2 : 1 }}
        >
          <motion.img
            src={it.src}
            alt={it.alt}
            className="w-full h-full object-cover"
            initial={false}
            animate={{ scale: idx === active && !reducedMotion ? 1.06 : 1 }}
            transition={{ duration: 6, ease: "linear" }}
            loading="eager"
            decoding="async"
            data-testid={`heritage-hero-slide-${idx}`}
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#1A2024]/30 via-transparent to-[#C05A3C]/15" />
        </motion.div>
      ))}

      {/* Active slide label */}
      <div className="absolute top-4 left-4 z-10">
        <span className="label-overline text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full text-[10px]">
          {images[active].label}
        </span>
      </div>

      {/* Dot navigation */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            aria-label={`Show slide ${i + 1}`}
            data-testid={`heritage-hero-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === active ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeritageHero;
